// @flow
import { useCallback, useContext, useMemo } from 'react';
import { FormStateContext } from 'contexts/FormStateContext';
import type { SectionNames } from 'constants/sectionNames';
import {
  getNewFormState,
  getNewFormValues,
} from 'hooks/affordaPortalFormState/utils';
import useEnableFormStateLogging from 'hooks/affordaPortalFormState/useEnableFormStateLogging';

const useFormStateManager = (sectionName: SectionNames) => {
  const { isFormStateLoggingEnabled } = useEnableFormStateLogging();

  const { data, update } = useContext(FormStateContext);

  const { formState } = useMemo(() => {
    const { formState, formValues } = data[sectionName] || {};

    if (isFormStateLoggingEnabled) {
      console.group(`Form State [${sectionName}]`);
      console.log('Initial State: ', formState);
      console.log('Initial Values: ', formValues);
      console.groupEnd();
    }

    return {
      formState,
      formValues,
    };
  }, [data, isFormStateLoggingEnabled, sectionName]);

  const setSectionData = useCallback(
    (
      callback: Object | ((prevState: Object) => Object),
      { control, fields }: { control: Object, fields: Array } | undefined = {},
    ) => {
      update((prevState) => {
        let newState = {};
        const sectionData = prevState[sectionName];

        if (typeof callback === 'function') {
          newState = callback(sectionData.formState);
        } else {
          newState = callback;
        }

        const newFormState = getNewFormState({
          fields,
          newState,
          prevState: sectionData.formState,
        });
        const newFormValues = getNewFormValues({
          control,
          fields,
          prevValues: sectionData.formValues,
        });

        const payload = {
          ...Object.fromEntries(
            Object.entries(prevState).filter(([key]) => key !== sectionName),
          ),
          [sectionName]: {
            formState: newFormState,
            formValues: newFormValues,
          },
        };

        if (isFormStateLoggingEnabled) {
          console.group('Form State Update');
          console.log('Before: ', prevState);
          console.log('Updates', { newFormState, newFormValues });
          console.log('After: ', payload);
          console.groupEnd();
        }

        return payload;
      });
    },
    [isFormStateLoggingEnabled, sectionName, update],
  );

  return {
    formState,
    updateForm: setSectionData,
  };
};

export default useFormStateManager;
